import React, { useState, useRef, useEffect } from 'react';
import JoditEditor, { Jodit } from 'jodit-react';
import { useTemplate } from "../../utils/Template";
import { Button, Modal, Divider, Form, Input, Select, Slider } from 'antd';
import FileBrowser from '../FileBrowser';
import { getColor, getFontFamily } from '../../utils/ConvertCSS';
import LinkEditor from './LinkEditor';
import ColourPicker from '../../globals/styles/ColourPicker';
import FontPicker from '../../globals/styles/FontPicker';

export default function TextEditor(props) {

  const [template, setTemplate] = useTemplate();

  let _bg_default = getColor(template.background_container_colour, template, "#fff");

  const { link_id = 0, background_colour = 0, background_colour_default = _bg_default, text_colour = false, font = false, expand = false, setExpand = () => { }, padding_x = 0, padding_y = 0, toolbar_size = "small", text_align = "left", paragraph_preset = 0, placeholder = "Write text....", value = "", onChange = () => { }, width = 900 } = props;

  const colors = template.colours;
  const fonts = template.fonts;


  const bg_color = getColor(background_colour, template, background_colour_default);
  const editorRef = useRef(null);
  const [joditInsance, setJoditInstance] = useState();
  const [open, setOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");

  const [savedSelection, setSavedSelection] = useState(null);
  const [fontOpen, setFontOpen] = useState("");
  const [fontValue, setFontValue] = useState({
    font: getFontFamily(fonts[0], template)
  })



  const [underlineOpen, setUnderlineOpen] = useState("");
  const [underlineValues, setUnderlineValues] = useState({
    thickness: 2,
    color: colors[0]?.id,
    offset: 2,
  });


  function getOrWrapSpan(editor) {
    const sel = editor.selection;
    const range = sel.range;
    let node = range.commonAncestorContainer;
    if (node.nodeType === 3) {
      node = node.parentNode;
    }
    if (node.tagName === 'SPAN') {
      return node;
    }
    const span = editor.create.element('span');
    range.surroundContents(span);
    return span;
  }


  Jodit.defaultOptions.controls.customUnderline = {
    name: 'customUnderline',
    tooltip: 'Underline styles',
    iconURL: '/create/icons/underline.svg',
    exec: (editor) => {
      setSavedSelection(editor.selection.save());
      setUnderlineOpen(true)
    },
  };

  Jodit.defaultOptions.controls.customFont = {
    name: 'customFont',
    tooltip: 'Font',
    iconURL: '/create/icons/font.svg',
    exec: (editor) => {
      setSavedSelection(editor.selection.save());
      setFontOpen(true)
    },
  };

  const applyUnderline = () => {
    if (!joditInsance || !savedSelection) return;

    joditInsance.selection.restore(savedSelection);

    const span = getOrWrapSpan(joditInsance);
    const { thickness, color, offset } = underlineValues;

    span.dataset.colorId = color;
    span.style.textDecorationLine = thickness ? 'underline' : 'none';
    span.style.textDecorationThickness = `${thickness}px`;
    span.style.textDecorationColor = getColor(color, template);
    span.style.textUnderlineOffset = `${offset}px`;

    setUnderlineOpen(false);
  };

  const applyFont = () => {
    if (!joditInsance || !savedSelection) return;

    joditInsance.selection.restore(savedSelection);

    const span = getOrWrapSpan(joditInsance);
    if (!span) return;

    const fontId = fontValue.font;
    span.dataset.fontId = fontId;
    span.style.fontFamily = `'${getFontFamily(fontId, template)}'`;

    setFontOpen(false);
  };


  const listColours = () => {
    let _ret = {}
    template.colours.forEach(item => {
      let color = getColor(item.id, template);
      _ret["bm-colour-" + item.id] = "<div style='display:flex; padding:2px; padding-top:0px;'><div style='margin-top:2px; display: inline-block; width: 15px; height: 15px; background-color: " + color + "; border: 1px solid #eee;'>&nbsp;</div> <small class='black-txt'>&nbsp;" + item.name + "</small></div>";
    })
    return _ret;
  }

  const listHighlights = () => {
    let _ret = {}
    template.colours.forEach(item => {
      let color = getColor(item.id, template);
      _ret["bm-highlight-" + item.id] = "<div style='display:flex; background-color:#fff; padding:2px; padding-top:0px;'><div style='margin-top:2px; display: inline-block; width: 15px; height: 15px; background-color: " + color + "; border: 1px solid #eee;'>&nbsp;</div> <small class='black-txt'>&nbsp;" + item.name + "</small></div>";
    })
    return _ret;
  }


  let buttons = [
    { name: "bold" },
    { name: "italic" },
    { name: "customUnderline" },
    { name: "customFont" }
  ];

  let sizes = {
    '12': '12',
    '16': '16'
  };



  if (toolbar_size !== "small") {
    buttons.push({
      name: 'fontSize',
      iconURL: '/create/icons/fontsize.svg',
      command: 'fontSize',
      list: ["8px", "9px", "10px", "11px", "12px", "14px", "16px", "18px", "24px", "30px", "32px", "34px", "36px", "48px", "60px", "72px", "96px"],
      isActiveChild: (editor, control) => {
        const fontSize = editor.selection.getHTML().match(/font-size: (\w+);/) || ['font-size: 14px', '14px'];
        return `${control.args[1]}` === fontSize[1];
      },
    })
    buttons.push({ name: "ul", list: false })
    buttons.push({ name: "ol", list: false })



    buttons.push({ name: "gallery", iconURL: '/create/icons/image.svg', exec: function (editor) { return setOpen(true); } })
    buttons.push({ name: "classSpan", tooltip: 'Text colour', iconURL: '/create/icons/colour.svg', list: listColours() })
    buttons.push({ name: "classSpan", tooltip: 'Highlight colour', iconURL: '/create/icons/highlight.svg', list: listHighlights() })
  } else {
    buttons.push({ name: "expand", iconURL: '/create/icons/more-options.png', exec: function (editor) { return setExpand(!expand); } })
  }


  buttons.push({
    name: 'customLink',
    group: 'insert',
    text: 'Insert Mailto',
    iconURL: '/create/icons/link.svg',
    exec: (editor) => {
      setLinkText(editor.selection.html)
      setLinkOpen(true);
    },
    tooltip: 'Insert mailto link'
  })


  const updateAlt = (imgTag, newAlt) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(imgTag, 'text/html');
    const imgElement = doc.querySelector('img');
    imgElement.setAttribute('alt', newAlt);
    return imgElement.outerHTML;
  }

  const getAlt = (imgTag) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(imgTag, 'text/html');
    const imgElement = doc.querySelector('img');
    const originalAlt = imgElement.getAttribute('alt') || '';
    return originalAlt;
  }

  useEffect(() => {


    if (editorRef.current) {
      const jodit = new Jodit(editorRef.current, {
        placeholder: placeholder,
        buttons: buttons,
        toolbarAdaptive: false,
        popup: {
          img: [{
            name: "Alt Text", exec: (e) => {
              const old_alt = getAlt(e.selection.html)
              const new_alt = prompt('Alt text:', old_alt);
              e.selection.insertHTML(updateAlt(e.selection.html, (new_alt) ? new_alt : old_alt));
            }
          }]
        },
        toolbarButtonSize: toolbar_size,
        disablePlugins: ["imageProperties", "paste"],
        height: (toolbar_size === "small") ? 260 : "auto"
      });




      var ele = document.querySelector(".jodit-workplace");
      ele.style.textAlign = text_align;
      ele.style.padding = padding_y + "px " + padding_x + "px";
      ele.style.backgroundColor = bg_color;

      if (text_colour) {
        ele.style.color = text_colour;
      }

      if (font) {
        ele.style.fontFamily = `'${getFontFamily(font, template)}'`;
      } else {
        jodit.editor.classList.add("bm-txt-" + paragraph_preset);
      }


      jodit.editor.classList.add("bm-lnkin-" + link_id);

      jodit.events.on('paste', function (event) {
        event.preventDefault();
        const plainText = (event.clipboardData || window.clipboardData).getData('text/plain');
        const html = plainText.split(/\r?\n\r?\n/).map(p => `<p>${p.replace(/\r?\n/g, "<br>")}</p>`).join("");
        jodit.selection.insertHTML(html);
      });




      jodit.events.on('dblclick', function (event) {
        var target = event.target;

        // Check if the double-clicked element is an image
        if (target && target.tagName === 'IMG') {
          // Prevent the default context menu
          event.preventDefault();
        }
      });

      jodit.setEditorValue(value);
      jodit.events.on('change', (e) => {
        onChange(e)
      });
      setJoditInstance(jodit)
    }
  }, []);





  const selectAlt = (_image) => {
    setOpen(false);
    let alt_text = "";
    const handleInputChange = (e) => {
      alt_text = e.target.value;
    };

    Modal.info({
      icon: <></>,
      title: "Please enter alt text",
      content: <>
        <Divider style={{ "margin": "15px 0px" }} dashed />
        <Form layout="vertical">
          <Form.Item name="alt" label="Alt text" rules={[{ required: true, message: "Alt text is required" }]}>
            <Input placeholder="Alt text..." onChange={handleInputChange} />
          </Form.Item>
        </Form>
      </>,
      okText: "",
      onOk: () => {
        callBack(_image, alt_text)
      }
    })

  }



  const callBack = (_img, _alt = "") => {
    setOpen(false)
    joditInsance.selection.insertHTML("<img alt='" + _alt + "' class='bm-img' src='https://bookmanager.com/i/" + window.san + _img.path + "' />")
  }

  const linkCallBack = (_f) => {

    const {
      protocol = "mailto:",
      url = "",
      email = "",
      phone = "",
      tab = true,
    } = _f;



    let _txt = linkText;
    if (_f.type === "url") {
      let _target = (tab) ? "_blank" : "_self"
      _txt = (_txt) ? _txt : url;
      joditInsance.selection.insertHTML("<a target='" + _target + "' href='" + protocol + url + "'>" + _txt + "</a>")
    } else if (_f.type === "phone") {

      _txt = (_txt) ? _txt : phone;
      joditInsance.selection.insertHTML("<a href='" + protocol + phone + "'>" + _txt + "</a>")
    } else {
      _txt = (_txt) ? _txt : email;
      joditInsance.selection.insertHTML("<a href='" + protocol + email + "'>" + _txt + "</a>")
    }

    setLinkOpen(false);
    setLinkText(false);

    //joditInsance.selection.insertHTML("<a href='/contact-hours'>faucibus.</a>")
  }

  const getSelection = () => {
    return joditInsance.selection;
  }


  return (
    <div>
      {/* Custom Underline */}
      <Modal open={underlineOpen} width={300} onOk={applyUnderline} onCancel={() => setUnderlineOpen(false)} destroyOnClose>
        <strong>Underline Styles</strong>
        <Divider dashed style={{ "margin": "15px 0px" }} />
        <Form layout="vertical">
          <Form.Item label={`Thickness (${underlineValues.thickness}px)`}>
            <Slider value={underlineValues.thickness} min={0} max={9} onChange={e => setUnderlineValues(v => ({ ...v, thickness: e }))} />
          </Form.Item>
          <Form.Item label={`Vertical offset (${underlineValues.offset}px)`}><Slider value={underlineValues.offset} min={-10} max={10} onChange={e => setUnderlineValues(v => ({ ...v, offset: e }))} />
          </Form.Item>
          <Form.Item label="Color">
            <ColourPicker value={underlineValues.color} onChange={(e) => setUnderlineValues(v => ({ ...v, color: e }))} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Custom Font */}
      <Modal open={fontOpen} width={300} onOk={applyFont} onCancel={() => setFontOpen(false)} destroyOnClose>
        <strong>Underline Styles</strong>
        <Divider dashed style={{ "margin": "15px 0px" }} />
        <Form layout="vertical">

          <Form.Item label="Color">
            <FontPicker value={fontValue?.font} onChange={(e) => {
              setFontValue(v => ({ ...v, font: e }))
            }} />

          </Form.Item>
        </Form>
      </Modal>

      <textarea style={{ "textAlign": "center" }} ref={editorRef} />
      <Modal destroyOnClose={true} title="Add Link" footer={null} width={350} open={linkOpen} onCancel={() => setLinkOpen(false)}>
        <LinkEditor getSelection={getSelection} onChange={linkCallBack} />
      </Modal>
      <Modal title="Media Gallery" footer={null} width={1000} open={open} onCancel={() => setOpen(false)}>
        <FileBrowser selectImage={selectAlt} />
      </Modal>
    </div>
  );
};

